import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Municipality } from "app/addresses/store/municipalities/municipalities.model";
import { LocalizedString } from "app/shared/model/localized-string.model";
import { Address, PhoneNumber } from "odq-toolbox";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { DownLoadFileService } from "~app/services/download-file.service";
import { environment } from "~env";
import { AdministrativeRegion, DecisionDocument, DecisionSanction, InsuranceStatusCode, PublicMember, PublicMemberInfos, Speciality, SpecialtyType, WorkAddress } from "./public-member-search.model";

const REF_TABLE_API_URL = environment.referenceTableQueryApi;
const PROJ_API_URL = environment.projectionQueryApi;
const PROJ_API = environment.projectionApi;

@Injectable({
  providedIn: 'root',
})
export class PublicMemberSearchQueryService {

  constructor(private translate: TranslateService, private http: HttpClient, private downLoadFileService: DownLoadFileService,) { }

  getAllSpecialities(): Observable<SpecialtyType[]> {
    const language = this.translate.currentLang.replace('fr', 'Fr').replace('en', 'En');

    return this.http.get(`${REF_TABLE_API_URL}/SpecialtyTypes?$filter=IsActive eq true&$orderby=Description/${language}`)
      .pipe(
        map(res => {
          return res['value']?.map(item => {
            return this.parseSpecialtyTypeObject(item);
          });
        }),
      )
  }
  private parseSpecialtyTypeObject(item: SpecialtyType): SpecialtyType {
    return {
      id: item['Id'],
      code: item['Code'],
      description: new LocalizedString(item['Description']['Fr'], item['Description']['En']),
      descriptionShort: item['DescriptionShort']
        ? new LocalizedString(item['DescriptionShort']['Fr'], item['DescriptionShort']['En'])
        : null,
      isActive: item['IsActive'],
    } as SpecialtyType;
  }


  searchMunicipalities(searchTerm: string): Observable<Municipality[]> {
    return this.http.get(`${PROJ_API_URL}/RegisteredMemberMunicipalities/?$orderby=Name&$top=10&$filter=contains(name,'${searchTerm}') and IsActive eq true and CountryIsoCode2 eq 'CA'`)
      .pipe(
        map(res => {
          return res['value']?.map(item => {
            return this.parseMunicipalityObject(item);
          });
        }),
      )
  }
  private parseMunicipalityObject(item: any): Municipality {
    return {
      id: item.Id,
      name: item.Name,
      code: item.Code,
      countryIsoCode2: item.CountryIsoCode2,
      countryName: item.CountryName,
      provinceIsoCode: item.ProvinceIsoCode,
      provinceName: item.ProvinceName,
      electoralRegionCode: item.ElectoralRegionCode,
      administrativeRegionCode: item.AdministrativeRegionCode,
      isActive: item.IsActive,
    } as Municipality;
  }

  getAllAdministrativeRegions(): Observable<AdministrativeRegion[]> {
    return this.http.get(`${REF_TABLE_API_URL}/AdministrativeRegions?$filter=IsActive eq true`)
      .pipe(
        map(res => {
          return res['value']?.map(item => {
            return this.parseAdministrativeRegionObject(item);
          });
        }),
      )
  }
  private parseAdministrativeRegionObject(item: any): AdministrativeRegion {
    return {
      id: item['Id'],
      code: item['Code'],
      description: item['Description'],
      isActive: item['IsActive'],
    } as AdministrativeRegion;
  }

  searchMemberBy(cityName: string,
    specialtyTypeId: string,
    administrativeRegionCode,
    isAnesthesiaHospital: boolean,
    isAnesthesiaPrivateClinic: boolean,
    isParticularMedicalCondition: boolean,
    isHomeVisit: boolean,
    isSleepApnea: boolean,
    isReducedMobility: boolean,
    isLiftPerson: boolean): Observable<PublicMember[]> {
    let callUrl = `${PROJ_API}/PublicMemberInfos/SearchPublicMembers?`
    if (!!cityName) {
      callUrl = `${callUrl}cityName=${cityName}&`;
    }
    if (!!specialtyTypeId) {
      callUrl = `${callUrl}specialtyTypeId=${specialtyTypeId}&`;
    }
    if (!!administrativeRegionCode) {
      callUrl = `${callUrl}administrativeRegionCode=${administrativeRegionCode}&`;
    }

    if (!!isAnesthesiaHospital) {
      callUrl = `${callUrl}isAnesthesiaHospital=${isAnesthesiaHospital}&`;
    }

    if (!!isAnesthesiaPrivateClinic) {
      callUrl = `${callUrl}isAnesthesiaPrivateClinic=${isAnesthesiaPrivateClinic}&`;
    }

    if (!!isParticularMedicalCondition) {
      callUrl = `${callUrl}isParticularMedicalCondition=${isParticularMedicalCondition}&`;
    }

    if (!!isHomeVisit) {
      callUrl = `${callUrl}isHomeVisit=${isHomeVisit}&`;
    }

    if (!!isSleepApnea) {
      callUrl = `${callUrl}isSleepApnea=${isSleepApnea}&`;
    }

    if (!!isReducedMobility) {
      callUrl = `${callUrl}isReducedMobility=${isReducedMobility}&`;
    }

    if (!!isLiftPerson) {
      callUrl = `${callUrl}isLiftPerson=${isLiftPerson}&`;
    }
    return this.http.get<PublicMember[]>(callUrl)
  }

  fetchPublicMemberInfos(memberId: string): Observable<PublicMemberInfos> {
    return this.http.get<PublicMember[]>(`${PROJ_API}/PublicMemberInfos?memberId=${memberId}`).pipe(
      map(res => this.parsePublicMemberInfos(res))
    )
  }
  private parsePublicMemberInfos(item: any): PublicMemberInfos {
    const result = {
      id: item?.memberId,
      firstName: item?.firstName,
      lastName: item?.lastName,
      permitNumber: item?.permitNumber,
      effectiveExerciseStatus: item?.effectiveExerciseStatus,
      effectiveInscriptionStatus: item?.effectiveInscriptionStatus,
      effectiveStatusDate: item?.effectiveStatusDate,
      effectiveInsuranceStatusCode: item?.effectiveInsuranceStatusCode,
      permitType: item?.permitType,
      decisions: this.parseDecisionSanctions(item?.decisions),
      professionalDomicile: {
        effectiveOn: new Date(item?.professionalDomicile?.effectiveOn),
        phone: !!item?.professionalDomicile?.phoneNumber ?
          new PhoneNumber(
            item?.professionalDomicile?.phoneCountryCode ?? 1,
            item?.professionalDomicile?.phoneNumber,
            item?.professionalDomicile?.phoneExt
          ) : null,
        fax: !!item?.professionalDomicile?.faxNumber ?
          new PhoneNumber(
            item?.professionalDomicile?.faxCountryCode ?? 1,
            item?.professionalDomicile?.faxNumber,
            item?.professionalDomicile?.faxExt
          ) : null,
        address: new Address(
          item?.professionalDomicile?.normalizeId,
          item?.professionalDomicile?.line1,
          item?.professionalDomicile?.line2,
          item?.professionalDomicile?.city,
          item?.professionalDomicile?.provinceCode,
          item?.professionalDomicile?.postalCode,
          item?.professionalDomicile?.countryCode
        ),
        practiceType: item?.professionalDomicile?.workAddressType,
        practiceSince: null,
        isInCompany: false,
        wheelchairAccess: item?.professionalDomicile?.wheelchairAccess,
        workAddressId: item?.professionalDomicile?.workAddressId,
        clinicName: item?.professionalDomicile?.clinicName,
        typeOfPlace: item?.professionalDomicile?.type.charAt(0).toLowerCase() + item?.professionalDomicile?.type.slice(1)
      },
      specialtiTypeIds: item.specialtiesTypeIds,
      limitations: item.limitations,
      activeWorkAddresses: this.parseWorkAdresses(item.workAddresses),
      specialities: this.parseSpecialities(item.specialities),
      firstInscriptionDate: item?.firstInscriptionDate,

      isAnesthesiaHospital: item?.isAnesthesiaHospital,
      isAnesthesiaPrivateClinic: item?.isAnesthesiaPrivateClinic,
      isParticularMedicalCondition: item?.isParticularMedicalCondition,
      isHomeVisit: item?.isHomeVisit,
      isSleepApnea: item?.isSleepApnea,
      isReducedMobility: item?.isReducedMobility,
      isLiftPerson: item?.isLiftPerson
    };

    return result;
  }

  private parseSpecialities(items: any[]): Speciality[] {
    if (items?.length < 1) {
      return [];
    }
    return items.map(item => ({
      obtainedDate: item?.obtainedDate,
      description: item?.description
    }))
  }
  private parseWorkAdresses(items: any[]): WorkAddress[] {
    if (items?.length < 1) {
      return [];
    }

    return items.map(item => ({
      clinicName: item?.clinicName,
      address: new Address(
        item?.id,
        item?.line1,
        item?.line2,
        item?.city,
        item?.provinceCode,
        item?.postalCode,
        item?.countryCode
      ),
      phone: item?.phoneNumber ? new PhoneNumber(
        item?.phoneCountryCode ?? 1,
        item?.phoneNumber,
        item?.phoneExt
      ) : null,
      fax: item?.faxNumber ? new PhoneNumber(
        item?.faxCountryCode ?? 1,
        item?.faxNumber,
        item?.faxExt
      ) : null,
      isWheelchairAccessible: item?.wheelchairAccess
    }));


  }

  private parseDecisionSanctions(items: any[]): DecisionSanction[] {
    if (items?.length == 0 || items == undefined) {
      return [];
    }

    return items.map(item => ({
      recordNumber: item.recordNumber,
      permitNumber: item.permitNumber,
      publicDecisionType: item.publicDecisionType,
      decisionDate: item.decisionDate,
      documents: this.parseDecisionDocuments(item.documents)
    }));
  }

  private parseDecisionDocuments(items: any[]): DecisionDocument[] {
    return items.map(item => ({
      id: item.id,
      decisionId: item.decisionId,
      documentId: item.documentId,
      documentName: item.documentName,
      documentPath: item.documentPath
    }));
  }

  getAllInsuranceStatusCodes(): Observable<InsuranceStatusCode[]> {
    return this.http.get(`${REF_TABLE_API_URL}/InsuranceStatusCodes?$filter=IsActive eq true`)
      .pipe(
        map(res => {
          return res['value']?.map(item => {
            return this.parseInsuranceStatusCode(item);
          });
        }),
      );
  }

  private parseInsuranceStatusCode(item: InsuranceStatusCode): InsuranceStatusCode {
    return {
      id: item['Id'],
      code: item['Code'],
      description: new LocalizedString(item['Description']['Fr'], item['Description']['En']),
      isActive: item['IsActive'],
    } as InsuranceStatusCode;
  }

}